import type { RxDocument } from 'rxdb';
import { useNavigate, useParams } from 'react-router-dom';

import {
  useRxDocument,
  useRxDocuments,
  useRXDeleteByIds,
  useRxPatchMutation,
  useRxCreateMutation,
  type RxQueryParams,
  type MutationOptions,
} from '@/hooks/rxdb-base';

import { useRxDb } from '@/providers/rxdb/rx-hooks';
import { detachTagFromDocs } from '@/providers/rxdb/tag-handler';

import { appRoutes } from '@/constants/routes';
import { tagDefaultColor } from '@flintmint/web-shared/constants/ui';

import { sortByIdOrder } from '@/utils/helper-utils';

import type { Tag } from '@flintmint/core-shared/rxdb';

// create

export const useTagCreate = () => {
  const { mutate: createMutation, isLoading } = useRxCreateMutation<Tag>('tags');

  const createWithDefaults = (tag: Partial<Tag>, mutationOptions?: MutationOptions) => {
    return createMutation(
      {
        numDocs: 0,
        ...tag,
        color: tag.color || tagDefaultColor,
      },
      mutationOptions
    );
  };

  return {
    isLoading,
    mutate: createWithDefaults,
  };
};

// read

export const useTags = (queryParams: RxQueryParams<Tag> = { sort: [{ createdAt: 'desc' }] }) => {
  const { data, isLoading } = useRxDocuments<Tag>('tags', queryParams);

  return {
    data: !!queryParams.ids ? sortByIdOrder<RxDocument<Tag>>(data, queryParams.ids) : data,
    isLoading,
  };
};

export const useTag = (id?: string) => useRxDocument<Tag>('tags', id);

export const useTagByNameLazy = () => {
  const { data: tags, isLoading } = useTags();

  const query = (name: string) => {
    return tags?.find((tag) => tag.name === name) ?? null;
  };

  return { query, isLoading };
};

// update

export const useTagPatch = () => useRxPatchMutation<Tag>();

// delete

export const useTagsDeleteByIds = () => {
  const navigate = useNavigate();
  const { tagId } = useParams();

  const { mutate, isLoading } = useRXDeleteByIds<Tag>('tags');

  const deleteTags = (ids: string[], mutationOptions: MutationOptions = {}) => {
    return mutate(ids, {
      ...mutationOptions,
      onSuccess: (result) => {
        tagId && ids.includes(tagId) && navigate(appRoutes.tags);
        mutationOptions.onSuccess?.(result);
      },
    });
  };

  return { mutate: deleteTags, isLoading };
};

// detach

export const useTagDetachFromAll = () => {
  const db = useRxDb();

  const mutate = async (tagId: string) => {
    if (!db) return;

    await detachTagFromDocs(db.collections, tagId);
  };

  return { mutate };
};
